import { useMemo } from 'react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui'
import { Plus, X } from 'lucide-react'
import { useAgentMemory, useAuth, useProject } from '@/hooks'
import type { PendingAgentMemoryEntry } from '@/hooks'
import { Permissions } from '@/lib/permissions'
import { MemoryEntryRow, MemoryTypeBadge } from './MemoryEntryRow'

interface AgentMemoryPanelProps {
  projectId: string
  agentId: string | null
  pendingEntries: PendingAgentMemoryEntry[]
  onRemovePending: (key: string) => void
  onCreateClick: () => void
  changesLocked: boolean
}

export function AgentMemoryPanel({ projectId, agentId, pendingEntries, onRemovePending, onCreateClick, changesLocked }: AgentMemoryPanelProps) {
  const { data: entries, isLoading, isError, refetch } = useAgentMemory(projectId, agentId ?? undefined)
  const { data: project } = useProject(projectId)
  const { hasPermission } = useAuth(projectId)
  const canManage = hasPermission(Permissions.Project.Agent.Update, projectId)

  const sorted = useMemo(
    () => [...(entries ?? [])].sort((a, b) => b.createdAt.localeCompare(a.createdAt)),
    [entries]
  )
  const total = sorted.length + pendingEntries.length

  return (
    <div className="flex min-h-[560px] flex-col space-y-3 rounded-2xl border border-border bg-card p-6" data-testid="agent-memory-panel">
      <div className="flex shrink-0 justify-between">
        <div className="flex items-center gap-2">
          <h2 className="text-lg font-bold text-foreground">Stored Memory</h2>
          <Badge variant="secondary">
            {total} {total === 1 ? 'entry' : 'entries'}
          </Badge>
        </div>

        {canManage && (
          <Button onClick={onCreateClick} disabled={changesLocked}>
            <Plus className="h-4 w-4" />
            New Memory
          </Button>
        )}
      </div>
      <p className="shrink-0 text-sm text-muted-foreground">
        What this agent remembers in {project?.name ?? 'this project'} — the project it runs in. Each entry is one fact or one piece of feedback (like the file-backed memory model), with where it came from. Hydrated into the container before a run, updated after.
      </p>
      {isLoading ? (
        <p className="flex-1 py-4 text-center text-sm text-muted-foreground" role="status" aria-live="polite" data-testid="agent-memory-loading">
          Loading memory…
        </p>
      ) : isError ? (
        <div
          role="alert"
          data-testid="agent-memory-error"
          className="flex flex-1 flex-col items-center justify-center gap-2 rounded-2xl border border-dashed border-border bg-table-row p-8 text-center text-sm text-destructive"
        >
          <span>Couldn't load this agent's memory.</span>
          <button className="underline" onClick={() => refetch()}>Retry</button>
        </div>
      ) : total === 0 ? (
        <div className="flex flex-1 items-center justify-center rounded-2xl border border-dashed border-border bg-table-row p-8 text-center text-sm text-muted-foreground" data-testid="agent-memory-empty">
          No stored memory yet. Memory accumulates after runs when the agent's memory scope is set to project.
        </div>
      ) : (
        <div className="flex flex-1 flex-col gap-2">
          {pendingEntries.map(entry => (
            <div key={entry.key} className="space-y-2 rounded-xl border border-dashed px-4 py-3" data-testid="agent-memory-pending-entry">
              <div className="flex items-start justify-between">
                <MemoryTypeBadge type={entry.type} />
                <div className="flex shrink-0 items-center gap-2">
                  <span className="text-xs text-muted-foreground">Not saved yet</span>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    aria-label={`Remove unsaved memory entry: ${entry.content.slice(0, 40)}`}
                    onClick={() => onRemovePending(entry.key)}
                    disabled={changesLocked}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <p className="whitespace-pre-wrap break-words text-sm text-muted-foreground">{entry.content}</p>
            </div>
          ))}
          {sorted.map(entry => (
            <MemoryEntryRow key={entry.id} projectId={projectId} entry={entry} canManage={canManage} />
          ))}
        </div>
      )}
    </div>
  )
}
